import { ArcRotateCamera } from '@babylonjs/core/Cameras/arcRotateCamera';
import { Camera } from '@babylonjs/core/Cameras/camera';
import { Gizmo } from '@babylonjs/core/Gizmos/gizmo';
import { Matrix, Quaternion, Vector3 } from '@babylonjs/core/Maths/math';
import { AbstractMesh } from '@babylonjs/core/Meshes/abstractMesh';
import { Mesh } from '@babylonjs/core/Meshes/mesh';
import { Observable } from '@babylonjs/core/Misc/observable';
import { WebXRCamera } from '@babylonjs/core/XR/webXRCamera';
import { Scene } from '@babylonjs/core/scene';
import { Kb3dObject } from '@models/classes/kb3d-object';
import { SpaceNode } from '@models/classes/meshes/space-node';
import { KbVector } from '@models/classes/primitives/kb-vector';
import { getCameraPositionWithOffset } from '@view/helpers/math-helper';
/**
 * Renders gizmos on top of an existing scene which provide controls for position, rotation, etc.
 */

export const AUTO_MESH_NAME = '_autoGizmoMesh';
export abstract class KbGizmo extends Gizmo {
    updateGizmoPosition() {
        this._update();
    }

    attachTo(node: Kb3dObject | KbVector) {}

    /** Fires an event when any of it's sub gizmos are dragged */
    onDragStartObservable: Observable<void>;
    /** Fires an event while any of it's sub gizmos are dragged */
    onDragObservable: Observable<any>;
    /** Fires an event when any of it's sub gizmos are released from dragging */
    onDragEndObservable: Observable<any>;

    get attachedMesh() {
        return super.attachedMesh;
    }
    set attachedMesh(m: AbstractMesh | null) {
        if (m) {
            super.attachedMesh = m;
        }
    }

    get visibility() {
        return this._rootMesh.visibility;
    }
    set visibility(visibility: number) {
        this._rootMesh.setEnabled(visibility === 1);
    }

    protected attachMeshAtNodeSpace(scene: Scene, target: SpaceNode | KbVector, positionSpaceTransform?: Matrix) {
        if (!this.attachedMesh) {
            this.attachedMesh = new Mesh(AUTO_MESH_NAME, scene);
        }
        let translation: Vector3;
        const rotation = Quaternion.Identity();
        const scale = Vector3.One();

        translation = target instanceof SpaceNode ? target.position.toVec3() : target.toVec3();
        if (positionSpaceTransform) {
            translation = Vector3.TransformCoordinates(translation, positionSpaceTransform);
            positionSpaceTransform.decompose(scale, rotation);
        }

        this.attachedMesh.position = translation;
        this.attachedMesh.rotationQuaternion = rotation;
        this.attachedMesh.scaling = scale;

        return this.attachedMesh;
    }

    protected _update() {
        if (this.attachedMesh) {
            const _tempVector = new Vector3();
            const _tmpMatrix = new Matrix();
            if (this.updateGizmoRotationToMatchAttachedMesh) {
                if (!this._rootMesh.rotationQuaternion) {
                    this._rootMesh.rotationQuaternion = Quaternion.RotationYawPitchRoll(
                        this._rootMesh.rotation.y,
                        this._rootMesh.rotation.x,
                        this._rootMesh.rotation.z
                    );
                }

                // Remove scaling before getting rotation matrix to get rotation matrix unmodified by scale
                _tempVector.copyFrom(this.attachedMesh.scaling);
                if (this.attachedMesh.scaling.x < 0) {
                    this.attachedMesh.scaling.x *= -1;
                }
                if (this.attachedMesh.scaling.y < 0) {
                    this.attachedMesh.scaling.y *= -1;
                }
                if (this.attachedMesh.scaling.z < 0) {
                    this.attachedMesh.scaling.z *= -1;
                }
                this.attachedMesh.computeWorldMatrix().getRotationMatrixToRef(_tmpMatrix);
                this.attachedMesh.scaling.copyFrom(_tempVector);
                this.attachedMesh.computeWorldMatrix();
                Quaternion.FromRotationMatrixToRef(_tmpMatrix, this._rootMesh.rotationQuaternion);
            }
            // else if (this._rootMesh.rotationQuaternion) {
            //     this._rootMesh.rotationQuaternion.set(0, 0, 0, 1);
            // }
            if (this.updateGizmoPositionToMatchAttachedMesh) {
                this._rootMesh.position.copyFrom(this.attachedMesh.absolutePosition);
            }
            if (this.updateScale && this.gizmoLayer.utilityLayerScene.activeCamera && this.attachedMesh) {
                let dist: number;
                const camera = this.gizmoLayer.utilityLayerScene.activeCamera;

                if (camera.mode === Camera.ORTHOGRAPHIC_CAMERA) {
                    dist = ((camera.orthoRight || 0) - (camera.orthoLeft || 0)) / 2;
                } else {
                    let cameraPosition: Vector3;
                    if (camera instanceof WebXRCamera && camera.globalPosition) {
                        cameraPosition = camera.globalPosition;
                    } else if (camera instanceof ArcRotateCamera) {
                        cameraPosition = getCameraPositionWithOffset(camera);
                    } else {
                        cameraPosition = camera.globalPosition;
                    }

                    this._rootMesh.position.subtractToRef(cameraPosition, _tempVector);
                    dist = _tempVector.length() * this.scaleRatio;
                }
                this._rootMesh.scaling.set(dist, dist, dist);
            }

            const pivot = this.attachedMesh.getPivotPoint();
            //let absolutePosition = this.attachedMesh.absolutePosition;

            // Absolute position is getting skewed because of the pivot point; need to get position without the rotation
            // if (pivot.length() > 0 && this.attachedMesh.rotationQuaternion) {
            //     var m = new Matrix();
            //     this.attachedMesh.rotationQuaternion.toRotationMatrix(m);
            //     absolutePosition = Vector3.TransformCoordinates(absolutePosition, m)
            // }
            // if (pivot.length() > 0) {
            //     absolutePosition = Vector3.Zero();
            // }

            this._rootMesh.position.copyFrom(Vector3.TransformCoordinates(pivot, this.attachedMesh.getWorldMatrix()));
        }
    }
}
